import type {CopilotAssignmentAttributes} from '@github-ui/agent-sessions/components/copilot-assignment/copilot-assignment-types'
import {isCopilot} from '@github-ui/assignees/copilot-user'
import type {IssueFieldWithUnsavedValue} from '@github-ui/issue-metadata/IssueFieldWithUnsavedValue'
import type {RepositoryPickerRepository} from '@github-ui/item-picker/RepositoryPicker'
import type {AssigneePickerAssignee} from '@github-ui/item-picker-assignee-relay'
import {readAssigneeFromFragment} from '@github-ui/item-picker-assignee-relay'
import type {IssueTypePickerIssueType} from '@github-ui/item-picker-issue-type-relay'
import type {LabelPickerLabel} from '@github-ui/item-picker-label-relay'
import type {MilestonePickerMilestone} from '@github-ui/item-picker-milestone'
import type {ProjectPickerRelayProject as ProjectPickerProject} from '@github-ui/item-picker-project-relay'
import {ssrSafeLocation} from '@github-ui/ssr-utils'
import {clearSessionStorage, useSessionStorage} from '@github-ui/use-safe-storage/session-storage'
import type React from 'react'
import {createContext, useCallback, useContext, useMemo, useState} from 'react'

import {storageKeys, storageMetadataKeys, storageTitleAndBodyKeys, VALUES} from '../constants/values'
import {getMetadataResetActions} from '../utils/metadata-reset'
import {
  BLANK_ISSUE_ID,
  instanceOfIssueFormData,
  instanceOfIssueTemplateData,
  type IssueCreatePayload,
} from '../utils/model'
import type {SafeOptionConfig} from '../utils/option-config'
import {useInitialProjects} from '../utils/project-initialization'

// This is a placeholder structural type until we have a Relay key type to reference,
// if we start rendering something sub-issues related in the UI (which we currently don't)
type Issue = {readonly id: string}

export type PreselectedDataTypes = {
  repository?: RepositoryPickerRepository
  template?: IssueCreatePayload

  parentIssue?: Issue
}

export type IssueCreateDataProviderProps = {
  optionConfig: SafeOptionConfig
  preselectedData: PreselectedDataTypes | undefined
  children: React.ReactNode
}

type IssueCreateDataContextState = {
  usedStorageKeyPrefix: string
  title: string
  setTitle: (value: string) => void
  reinitTitle: (value: string) => void

  body: string
  setBody: (value: string) => void
  reinitBody: (value: string) => void

  repository: RepositoryPickerRepository | undefined
  setRepository: (value: RepositoryPickerRepository | undefined) => void

  repositoryAbsolutePath: string

  template: IssueCreatePayload | undefined
  setTemplate: (value: IssueCreatePayload | undefined) => void

  preselectedRepository?: RepositoryPickerRepository | undefined
  preselectedTemplate?: IssueCreatePayload | undefined

  labels: LabelPickerLabel[]
  setLabels: (value: LabelPickerLabel[]) => void
  reinitLabels: (value: LabelPickerLabel[]) => void

  assignees: AssigneePickerAssignee[]
  setAssignees: (value: AssigneePickerAssignee[]) => void
  reinitAssignees: (value: AssigneePickerAssignee[]) => void

  projects: ProjectPickerProject[]
  setProjects: (value: ProjectPickerProject[]) => void
  reinitProjects: (value: ProjectPickerProject[]) => void

  milestone: MilestonePickerMilestone | null
  setMilestone: (value: MilestonePickerMilestone | null) => void
  reinitMilestone: (value: MilestonePickerMilestone | null) => void

  issueType: IssueTypePickerIssueType | null
  setIssueType: (value: IssueTypePickerIssueType | null) => void
  reinitIssueType: (value: IssueTypePickerIssueType | null) => void

  issueFields: IssueFieldWithUnsavedValue[]
  setIssueFields: (value: IssueFieldWithUnsavedValue[]) => void

  parentIssue: Issue | null

  originalTitle: string
  setOriginalTitle: (value: string) => void

  originalBody: string
  setOriginalBody: (value: string) => void

  setNewTitle: (newTitle: string) => void
  setNewBody: (newBody: string) => void

  resetMetadata: (options?: {
    repository?: {owner: {login: string}; name: string} | null
    selectedRepository?: {owner: {login: string}; name: string} | null
  }) => void
  clearSessionData: () => void
  clearSessionMetadata: () => void
  clearSessionTitleAndBody: () => void
  clearSessionCreateMore: () => void
  clearTitleAndBody: () => void

  isCrossRepositoryDialogOpen: boolean
  setIsCrossRepositoryDialogOpen: (value: boolean) => void

  copilotAssignmentAttributes: CopilotAssignmentAttributes | null
  setCopilotAssignmentAttributes: (value: CopilotAssignmentAttributes | null) => void
}

const IssueCreateDataContext = createContext<IssueCreateDataContextState | null>(null)

function getLabelsFromTemplate(template: IssueCreatePayload): LabelPickerLabel[] {
  let initialLabels: LabelPickerLabel[] = []
  if (template?.data) {
    initialLabels = template?.data.labels?.edges?.flatMap(e => (e?.node ? [e?.node] : [])) || []
  }
  return initialLabels
}

function getAssigneesFromTemplate(template: IssueCreatePayload): AssigneePickerAssignee[] {
  let initialAssignees: AssigneePickerAssignee[] = []
  if (template?.data) {
    initialAssignees =
      template?.data.assignees?.edges?.flatMap(e => (e?.node ? [readAssigneeFromFragment(e?.node)] : [])) || []
  }
  return initialAssignees
}

function getTitleFromTemplate(template: IssueCreatePayload): string {
  let initialTitle = ''
  if (template?.data) {
    initialTitle = template?.data.title || ''
  }
  return initialTitle
}

function getBodyFromTemplate(template: IssueCreatePayload): string {
  let initialBody = ''
  if (template?.data && instanceOfIssueTemplateData(template?.data)) {
    initialBody = template?.data.body || ''
  }
  return initialBody
}

function getIssueTypeFromTemplate(template: IssueCreatePayload): IssueTypePickerIssueType | null {
  let initialIssueType: IssueTypePickerIssueType | null = null
  if (template?.data && (instanceOfIssueFormData(template?.data) || instanceOfIssueTemplateData(template?.data))) {
    initialIssueType = template?.data.type || null
  }
  return initialIssueType
}

export function IssueCreateDataContextProvider({
  optionConfig,
  preselectedData,
  children,
}: IssueCreateDataProviderProps) {
  const {storageKeyPrefix, issueCreateArguments} = optionConfig
  const {issueTitle, issueBody} = VALUES.localTitleAndBodyStorageKeys
  const {issueLabels, issueAssignees, issueProjects, issueMilestone, issueIssueType, issueIssueFields} =
    VALUES.localStorageMetadataKeys

  const [repository, setRepository] = useState<RepositoryPickerRepository | undefined>(preselectedData?.repository)
  const [template, setTemplateInternal] = useState<IssueCreatePayload | undefined>(preselectedData?.template)

  // add the template name to the storage key prefix to avoid conflicts between different templates
  const [usedStorageKeyPrefix, setUsedStorageKeyPrefix] = useState<string>(
    `${storageKeyPrefix}.${template?.fileName || BLANK_ISSUE_ID}`,
  )

  let initialTitle = ''
  if (issueCreateArguments?.initialValues?.discussion) {
    initialTitle = issueCreateArguments?.initialValues?.discussion.title
  } else if (issueCreateArguments?.initialValues?.title) {
    initialTitle = issueCreateArguments?.initialValues?.title
  } else if (template) {
    const templateTitle = getTitleFromTemplate(template)
    let userInputTitle = ''
    if (issueCreateArguments?.initialValues?.appendTitleToTemplate) {
      userInputTitle = ` ${issueCreateArguments?.initialValues?.appendTitleToTemplate}`
    }
    initialTitle = [templateTitle, userInputTitle].join('').trimStart()
  } else {
    if (issueCreateArguments?.initialValues?.appendTitleToTemplate) {
      initialTitle = issueCreateArguments?.initialValues?.appendTitleToTemplate
    }
  }
  const [title, setTitle, reinitTitle] = useSessionStorage<string>(issueTitle(usedStorageKeyPrefix), initialTitle)

  let initialBody = ''
  if (issueCreateArguments?.initialValues?.discussion) {
    initialBody = issueCreateArguments?.initialValues?.discussion.formattedBody
  } else if (issueCreateArguments?.initialValues?.body) {
    initialBody = issueCreateArguments?.initialValues?.body
  } else if (template) {
    initialBody = getBodyFromTemplate(template)
  }
  const [body, setBody, reinitBody] = useSessionStorage<string>(issueBody(usedStorageKeyPrefix), initialBody)

  let initialLabels: LabelPickerLabel[] = []
  if (issueCreateArguments?.initialValues?.discussion) {
    initialLabels = issueCreateArguments?.initialValues?.discussion?.labels || []
  } else if (issueCreateArguments?.initialValues?.labels) {
    initialLabels = issueCreateArguments?.initialValues?.labels
  } else if (template) {
    initialLabels = getLabelsFromTemplate(template)
  }
  const [labels, setLabels, reinitLabels] = useSessionStorage<LabelPickerLabel[]>(
    issueLabels(usedStorageKeyPrefix),
    initialLabels,
  )

  let initialAssignees: AssigneePickerAssignee[] = []
  if (issueCreateArguments?.initialValues?.assignees) {
    initialAssignees = issueCreateArguments?.initialValues?.assignees
  } else if (template) {
    initialAssignees = getAssigneesFromTemplate(template)
  }
  const [assignees, _setAssignees, reinitAssignees] = useSessionStorage<AssigneePickerAssignee[]>(
    issueAssignees(usedStorageKeyPrefix),
    initialAssignees,
  )

  // Store the original preselected values for restoration when needed
  const preselectedProjects = useMemo(
    () => issueCreateArguments?.initialValues?.projects ?? [],
    [issueCreateArguments?.initialValues?.projects],
  )

  // For projects initialization, distinguish between explicit user intent and inheritance
  // Priority order:
  // 1. URL params/explicit projects (highest precedence)
  // 2. Template projects (takes precedence over parent inheritance)
  // 3. Parent projects (inheritance from parent issues)
  // 4. Empty array (final fallback)
  const initialProjects = useInitialProjects({
    template,
    projects: issueCreateArguments?.initialValues?.projects ?? [],
    isParentInheritance: !!issueCreateArguments?.parentIssue,
  })

  const [projects, setProjects, reinitProjects] = useSessionStorage<ProjectPickerProject[]>(
    issueProjects(usedStorageKeyPrefix),
    initialProjects,
  )

  const preselectedMilestone = useMemo(
    () => issueCreateArguments?.initialValues?.milestone ?? null,
    [issueCreateArguments?.initialValues?.milestone],
  )

  // For milestone initialization, only use preselected value if it should be preserved
  const initialMilestone = useMemo(() => {
    if (!preselectedMilestone || !preselectedData?.parentIssue) {
      return preselectedMilestone
    }

    // For sub-issues, check if milestone should be preserved based on repository context
    const actions = getMetadataResetActions(true, [], preselectedMilestone, {
      repository: preselectedData?.repository,
      selectedRepository: repository,
    })
    return actions.milestoneToSet
  }, [preselectedMilestone, preselectedData?.parentIssue, preselectedData?.repository, repository])

  const [milestone, setMilestone, reinitMilestone] = useSessionStorage<MilestonePickerMilestone | null>(
    issueMilestone(usedStorageKeyPrefix),
    initialMilestone,
  )

  let initialIssueType: IssueTypePickerIssueType | null = null
  if (issueCreateArguments?.initialValues?.type) {
    initialIssueType = issueCreateArguments?.initialValues?.type
  } else if (template) {
    initialIssueType = getIssueTypeFromTemplate(template)
  }
  const [issueType, setIssueType, reinitIssueType] = useSessionStorage<IssueTypePickerIssueType | null>(
    issueIssueType(usedStorageKeyPrefix),
    initialIssueType,
  )

  const [issueFields, setIssueFields] = useSessionStorage<IssueFieldWithUnsavedValue[]>(
    issueIssueFields(usedStorageKeyPrefix),
    [],
  )

  const [isCrossRepositoryDialogOpen, setIsCrossRepositoryDialogOpen] = useState<boolean>(false)
  const [copilotAssignmentAttributes, setCopilotAssignmentAttributes] = useState<CopilotAssignmentAttributes | null>(
    null,
  )

  const [originalTitle, setOriginalTitle] = useState<string>(title)
  const [originalBody, setOriginalBody] = useState<string>(body)

  const setNewTitle = useCallback(
    (newTitle: string) => {
      setTitle(newTitle)
      setOriginalTitle(newTitle)
    },
    [setTitle, setOriginalTitle],
  )
  const setNewBody = useCallback(
    (newBody: string) => {
      setBody(newBody)
      setOriginalBody(newBody)
    },
    [setBody, setOriginalBody],
  )
  const setAssignees = useCallback(
    (newAssignees: AssigneePickerAssignee[]) => {
      _setAssignees(newAssignees)
      const hasCopilotAssignee = newAssignees.some(assignee => isCopilot(assignee.login))
      if (hasCopilotAssignee) {
        setIsCrossRepositoryDialogOpen(true)
      }
    },
    [_setAssignees],
  )

  const resetMetadata = useCallback(
    (options?: {
      repository?: {owner: {login: string}; name: string} | null
      selectedRepository?: {owner: {login: string}; name: string} | null
    }) => {
      // Always clear these
      setAssignees([])
      setLabels([])
      setIssueFields([])

      const isSubIssue = !!preselectedData?.parentIssue

      // Use our tested logic to determine what to preserve
      const actions = getMetadataResetActions(isSubIssue, preselectedProjects, preselectedMilestone, options)

      setProjects(actions.projectsToSet)
      setMilestone(actions.milestoneToSet)
    },
    [
      setAssignees,
      setLabels,
      setIssueFields,
      preselectedData?.parentIssue,
      preselectedMilestone,
      preselectedProjects,
      setProjects,
      setMilestone,
    ],
  )

  const clearSessionData = useCallback(() => {
    clearSessionStorage(storageKeys(usedStorageKeyPrefix))
  }, [usedStorageKeyPrefix])

  const clearSessionMetadata = useCallback(() => {
    clearSessionStorage(storageMetadataKeys(usedStorageKeyPrefix))
  }, [usedStorageKeyPrefix])

  const clearSessionTitleAndBody = useCallback(() => {
    clearSessionStorage(storageTitleAndBodyKeys(usedStorageKeyPrefix))
  }, [usedStorageKeyPrefix])

  const clearSessionCreateMore = useCallback(() => {
    clearSessionStorage([`config.${VALUES.localStorageKeys.issueCreateMore(storageKeyPrefix)}`])
  }, [storageKeyPrefix])

  const clearTitleAndBody = useCallback(() => {
    setTitle('')
    setBody('')
    setOriginalTitle('')
    setOriginalBody('')
  }, [setTitle, setBody, setOriginalTitle, setOriginalBody])

  const origin = ssrSafeLocation?.origin ?? ''
  const repoNwo = repository?.nameWithOwner ?? ''
  const repositoryAbsolutePath = useMemo(() => `${origin}/${repoNwo}`, [origin, repoNwo])

  const parentIssue = preselectedData?.parentIssue ?? null

  const setTemplate = useCallback(
    (newTemplate: IssueCreatePayload | undefined) => {
      setTemplateInternal(newTemplate)
      setUsedStorageKeyPrefix(`${storageKeyPrefix}.${newTemplate?.fileName || BLANK_ISSUE_ID}`)
    },
    [storageKeyPrefix],
  )

  const contextValue: IssueCreateDataContextState = useMemo(() => {
    return {
      reinitTitle,
      reinitBody,
      reinitAssignees,
      reinitLabels,
      reinitProjects,
      reinitMilestone,
      reinitIssueType,
      usedStorageKeyPrefix,
      clearSessionData,
      clearSessionMetadata,
      clearSessionTitleAndBody,
      clearSessionCreateMore,
      resetMetadata,
      clearTitleAndBody,
      setNewTitle,
      setNewBody,

      originalTitle,
      setOriginalTitle,
      originalBody,
      setOriginalBody,
      title,
      setTitle,
      body,
      setBody,
      repository,
      setRepository,
      repositoryAbsolutePath,
      template,
      setTemplate,
      preselectedRepository: preselectedData?.repository,
      preselectedTemplate: preselectedData?.template,
      labels,
      setLabels,
      assignees,
      setAssignees,
      projects,
      setProjects,
      milestone,
      setMilestone,
      issueType,
      setIssueType,
      issueFields,
      setIssueFields,
      parentIssue,
      isCrossRepositoryDialogOpen,
      setIsCrossRepositoryDialogOpen,
      copilotAssignmentAttributes,
      setCopilotAssignmentAttributes,
    }
  }, [
    reinitTitle,
    reinitBody,
    reinitAssignees,
    reinitLabels,
    reinitProjects,
    reinitMilestone,
    reinitIssueType,
    usedStorageKeyPrefix,
    clearSessionData,
    clearSessionMetadata,
    clearSessionTitleAndBody,
    clearSessionCreateMore,
    resetMetadata,
    clearTitleAndBody,
    setNewTitle,
    setNewBody,
    originalTitle,
    originalBody,
    title,
    setTitle,
    body,
    setBody,
    repository,
    repositoryAbsolutePath,
    template,
    setTemplate,
    preselectedData?.repository,
    preselectedData?.template,
    labels,
    setLabels,
    assignees,
    setAssignees,
    projects,
    setProjects,
    milestone,
    setMilestone,
    issueType,
    setIssueType,
    issueFields,
    setIssueFields,
    parentIssue,
    isCrossRepositoryDialogOpen,
    copilotAssignmentAttributes,
  ])

  return <IssueCreateDataContext.Provider value={contextValue}>{children}</IssueCreateDataContext.Provider>
}

export const useIssueCreateDataContext = () => {
  const context = useContext(IssueCreateDataContext)
  if (!context) {
    throw new Error('useIssueCreateDataContext must be used within a IssueCreateDataContextProvider.')
  }

  return context
}

try{ IssueCreateDataContext.displayName ||= 'IssueCreateDataContext' } catch {}
try{ IssueCreateDataContextProvider.displayName ||= 'IssueCreateDataContextProvider' } catch {}